import { createSlice, current } from '@reduxjs/toolkit';
import app from 'app/config/app';
import Response from 'app/config/Response';
import isEmpty from 'helpers/isEmpty';

const initialState = {
  index: {
    data: [],
    params: {
      page_size: app.perPage || 10,
      page_number: 1,
      sort_by: 'created_at',
      order_by: 'desc',
    },
    isLoading: false,
    totalCount: 0,
    totalPage: 0,
    isError: false,
    errorCode: null,
    errorMessage: null,
  },
  create: {
    showProcessingModal: false,
    isCreating: false,
    isCreated: false,
    isFailed: false,
    errors: null,
    data: null,
    retries: 0,
    errorMessage: null,
  },

  update: {
    showProcessingModal: false,
    isUpdating: false,
    isUpdated: false,
    isFailed: false,
    errors: null,
    data: null,
    retries: 0,
    errorMessage: null,
  },
  show: {
    showBriefDetailModal: false,
    roleId: null,
    isLoading: false,
    isError: false,
    errorCode: null,
    errorMessage: null,
    data: null,
  },
  menus: {
    data: [],
    isLoading: false,
    isError: false,
    errorCode: null,
    errorMessage: null,
  },
  menuDetails: {
    data: [],
    isLoading: false,
    isError: false,
    errorCode: null,
    errorMessage: null,
  },
  roleClaim: {
    success: false,
  },
  roleDropdown: {
    data: [],
    isLoading: false,
    isError: false,
    errorMessage: null,
  },
  userRolesAndPermissions: {
    loading: false,
    success: false,
    response: null,
    error: null,
  },
  auditLogs: {
    data: [],
    params: {
      page_number: 1,
      page_size: 10,
    },
    pagination: {
      totalCount: 0,
      totalPage: 0,
      currentPage: 1,
      pageSize: 10,
    },
    isLoading: false,
    isError: false,
    errorMessage: null,
  },
};

export const roleAndPermissionsSlice = createSlice({
  name: 'rolesAndPermissions',
  initialState,
  reducers: {
    getRolesInit: (state) => {
      state.index = {
        ...state.index,
        isLoading: true,
        isError: false,
        errorCode: null,
        errorMessage: null,
      };
    },
    getRolesSuccess: (state, { payload }) => {
      let { data, pagination } = payload;

      const { totalCount, totalPage, currentPage, pageSize } = pagination || {
        totalCount: data.length,
        totalPage: 1,
        currentPage: 1,
        pageSize: data.length,
      };

      let startSN = (currentPage - 1) * pageSize;

      data = data?.map((item) => ({ sn: (startSN += 1), ...item }));

      state.index = {
        ...state.index,
        isLoading: false,
        isError: false,
        errorCode: null,
        errorMessage: null,
        data,
        totalCount,
        totalPage,
      };
    },

    getRolesFailure: (state, { payload }) => {
      state.index = {
        ...state.index,
        isLoading: false,
        isError: true,
        errorCode: payload?.response?.status ?? 0,
        errorMessage: payload?.response?.message,
      };
    },

    resetGetRolesFailure: (state) => {
      state.index = {
        ...state.index,
        isLoading: false,
        isError: false,
        errorCode: null,
        errorMessage: null,
      };
    },

    addRolesInit: (state) => {
      state.create = {
        ...state.create,
        showProcessingModal: true,
        isCreating: true,
        isCreated: false,
        isFailed: false,
        errors: null,
        data: null,
        errorMessage: null,
      };
    },
    addRolesSuccess: (state, { payload }) => {
      state.create = {
        ...state.create,
        showProcessingModal: false,
        isCreating: false,
        isCreated: true,
        isFailed: false,
        errors: null,
        data: payload.data,
      };
    },
    addRolesFailure: (state, { payload }) => {
      state.create = {
        ...state.create,
        showProcessingModal: false,
        isCreating: false,
        isCreated: false,
        isFailed: true,
        errors: payload?.response?.errors,
        errorMessage: payload?.response?.message,
      };
    },
    retryToCreate: (state) => {
      state.create.retries += 1;
    },
    resetCreate: (state) => {
      state.create = {
        ...initialState.create,
      };
    },
    setShowCreateProcessingModal: (state, { payload }) => {
      state.create.showProcessingModal = payload;
    },

    updateRolesInit: (state) => {
      state.update = {
        ...state.update,
        showProcessingModal: true,
        isUpdating: true,
        isUpdated: false,
        isFailed: false,
        errors: null,
        data: null,
        errorMessage: null,
      };
    },

    updateRolesSuccess: (state, { payload }) => {
      state.update = {
        ...state.update,
        showProcessingModal: false,
        isUpdating: false,
        isUpdated: true,
        isFailed: false,
        errors: null,
        data: payload.data,
      };
    },

    updateRolesFailure: (state, { payload }) => {
      state.update = {
        ...state.update,
        showProcessingModal: false,
        isUpdating: false,
        isUpdated: false,
        isFailed: true,
        errors: payload?.response?.errors,
        errorMessage: payload?.response?.message || 'Failed to update Role.',
      };
    },

    retryToUpdate: (state) => {
      state.update.retries += 1;
    },

    resetUpdate: (state) => {
      state.update = { ...initialState.update };
    },

    setShowEditProcessingModal: (state, { payload }) => {
      state.update.showProcessingModal = payload;
    },

    setParam: (state, { payload }) => {
      state.index.params = {
        ...state.index.params,
        ...payload,
      };
    },

    resetParams: (state) => {
      state.index.params = {
        page_size: state.index.params.page_size,
        page_number: 1,
      };
    },

    deleteFilterParams: (state, { payload }) => {
      if (Array.isArray(payload)) payload.forEach((key) => delete state.index.params[key]);
      else delete state.index.params[payload];
    },

    getRoleInit: (state) => {
      state.show = {
        ...state.show,
        isLoading: true,
        isError: false,
        errorCode: null,
        errorMessage: null,
      };
    },

    getRoleSuccess: (state, { payload }) => {
      state.show = {
        ...state.show,
        isLoading: false,
        isError: false,
        errorCode: null,
        errorMessage: null,
        data: payload.data,
      };
    },

    getRoleFailure: (state, { payload }) => {
      state.show = {
        ...state.show,
        isLoading: false,
        isError: true,
        errorCode: payload?.response?.status ?? 0,
        errorMessage: payload?.response?.message,
        data: null,
      };
    },

    resetGetRoleFailure: (state, { payload }) => {
      state.show = {
        ...state.show,
        isLoading: false,
        isError: false,
        errorCode: null,
        errorMessage: null,
        data: null,
      };
    },

    getRolesBriefDetailsInit: (state, { payload }) => {
      state.show = {
        ...state.show,
        showBriefDetailModal: payload.showBriefDetailModal,
        roleId: payload.roleId,
      };
    },

    hideRolesBriefDetails: (state) => {
      state.show = {
        showBriefDetailModal: false,
        rolesId: null,
        isLoading: false,
        isError: false,
        errorCode: null,
        errorMessage: null,
        data: null,
      };
    },
    getMenusInit: (state) => {
      state.menus = {
        ...state.menus,
        isLoading: true,
        isError: false,
        errorCode: null,
        errorMessage: null,
      };
    },
    getMenusSuccess: (state, { payload }) => {
      state.menus = {
        ...state.menus,
        isLoading: false,
        isError: false,
        errorCode: null,
        errorMessage: null,
        data: payload || [],
      };
    },
    getMenusFailure: (state, { payload }) => {
      state.menus = {
        ...state.menus,
        isLoading: false,
        isError: true,
        errorCode: payload?.response?.status ?? 0,
        errorMessage: payload?.response?.message,
      };
    },
    getMenuDetailsInit: (state) => {
      state.menuDetails = {
        ...state.menuDetails,
        isLoading: true,
        isError: false,
        errorCode: null,
        errorMessage: null,
      };
    },
    getMenuDetailsSuccess: (state, { payload }) => {
      state.menuDetails = {
        ...state.menuDetails,
        isLoading: false,
        isError: false,
        errorCode: null,
        errorMessage: null,
        data: payload || [],
      };
    },
    getMenuDetailsFailure: (state, { payload }) => {
      state.menuDetails = {
        ...state.menuDetails,
        isLoading: false,
        isError: true,
        errorCode: payload?.response?.status ?? 0,
        errorMessage: payload?.response?.message,
      };
    },
    createRoleClaimSuccess: (state) => {
      state.roleClaim.success = true;
    },
    resetRoleClaim: (state) => {
      state.roleClaim = { success: false };
    },
    getRoleDropdownInit: (state) => {
      state.roleDropdown = {
        ...state.roleDropdown,
        isLoading: true,
        isError: false,
        errorMessage: null,
      };
    },
    getRoleDropdownSuccess: (state, { payload }) => {
      state.roleDropdown = {
        ...state.roleDropdown,
        isLoading: false,
        isError: false,
        errorMessage: null,
        data: payload || [],
      };
    },
    getRoleDropdownFailure: (state, { payload }) => {
      state.roleDropdown = {
        ...state.roleDropdown,
        isLoading: false,
        isError: true,
        errorMessage: payload?.response?.message || 'Failed to load roles.',
      };
    },
    getUserRolesAndPermissionsInit: (state) => {
      state.userRolesAndPermissions = {
        ...state.userRolesAndPermissions,
        loading: true,
        success: false,
        error: null,
      };
    },
    getUserRolesAndPermissionsSuccess: (state, { payload }) => {
      state.userRolesAndPermissions = {
        ...state.userRolesAndPermissions,
        loading: false,
        success: true,
        response: payload,
        error: null,
      };
    },
    getUserRolesAndPermissionsFailure: (state, { payload }) => {
      state.userRolesAndPermissions = {
        ...state.userRolesAndPermissions,
        loading: false,
        success: false,
        error: payload,
      };
    },
    resetUserRolesAndPermissions: (state) => {
      state.userRolesAndPermissions = {
        ...initialState.userRolesAndPermissions,
      };
    },
    getAuditLogsInit: (state) => {
      state.auditLogs = {
        ...state.auditLogs,
        isLoading: true,
        isError: false,
        errorMessage: null,
      };
    },
    getAuditLogsSuccess: (state, { payload }) => {
      let { data, pagination } = payload;

      const { currentPage, pageSize } = pagination;

      let startSN = (currentPage - 1) * pageSize;

      data = data?.map((item) => ({ sn: (startSN += 1), ...item }));
      state.auditLogs = {
        ...state.auditLogs,
        isLoading: false,
        isError: false,
        errorMessage: null,
        data,
        pagination: payload?.pagination,
      };
    },
    getAuditLogsFailure: (state, { payload }) => {
      state.auditLogs = {
        ...state.auditLogs,
        isLoading: false,
        isError: true,
        errorMessage: payload?.response?.message,
      };
    },
    setAuditLogParam: (state, { payload }) => {
      state.auditLogs.params = {
        ...state.auditLogs.params,
        ...payload,
      };
    },
    resetAuditLogs: (state) => {
      state.auditLogs = {
        ...initialState.auditLogs,
      };
    },
  },
});

export const {
  getRolesInit,
  getRolesSuccess,
  getRolesFailure,
  resetGetRolesFailure,
  addRolesInit,
  addRolesSuccess,
  addRolesFailure,
  retryToCreate,
  resetCreate,
  setShowCreateProcessingModal,
  updateRolesInit,
  updateRolesSuccess,
  updateRolesFailure,
  retryToUpdate,
  resetUpdate,
  setShowEditProcessingModal,
  setParam,
  resetParams,
  deleteFilterParams,
  getRoleInit,
  getRoleSuccess,
  getRoleFailure,
  resetGetRoleFailure,
  getRolesBriefDetailsInit,
  hideRolesBriefDetails,
  getMenusInit,
  getMenusSuccess,
  getMenusFailure,
  getMenuDetailsInit,
  getMenuDetailsSuccess,
  getMenuDetailsFailure,
  createRoleClaimSuccess,
  resetRoleClaim,
  getRoleDropdownInit,
  getRoleDropdownSuccess,
  getRoleDropdownFailure,
  getUserRolesAndPermissionsInit,
  getUserRolesAndPermissionsSuccess,
  getUserRolesAndPermissionsFailure,
  resetUserRolesAndPermissions,
  getAuditLogsInit,
  getAuditLogsSuccess,
  getAuditLogsFailure,
  setAuditLogParam,
  resetAuditLogs,
} = roleAndPermissionsSlice.actions;

export default roleAndPermissionsSlice.reducer;
